import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import './ServicesPage.css';

const ServicesPage = () => {
  const services = [
    {
      icon: '🤖',
      title: 'Robotics Platform Development',
      description: 'Building robot systems for the physical AI future',
      details: [
        'AI-driven autonomy and orchestration',
        'Replay, auditability, and safety design',
        'Fleet operations and coordination',
        'Hardware-software integration',
        'Sensor fusion and perception systems',
        'Production deployment and operations'
      ]
    },
    {
      icon: '🧠',
      title: 'Physical AI Systems',
      description: 'AI-first robotics where autonomy is the hard problem',
      details: [
        'AI agent integration and governance',
        'Safety constraints and runtime enforcement',
        'Action logging and replay systems',
        'Edge AI deployment and optimization',
        'Computer vision and perception',
        'Multi-agent coordination'
      ]
    },
    {
      icon: '🛠️',
      title: 'Platform Engineering',
      description: 'Software-first approach to robotics development',
      details: [
        'Distributed systems architecture',
        'Message-based orchestration (NATS, etc)',
        'Observability and monitoring',
        'Progressive deployment strategies',
        'AI-augmented development workflows',
        'Team training and best practices'
      ]
    }
  ];

  const aiTools = [
    {
      category: 'Platform Architecture',
      tools: [
        { name: 'AI-First Design', desc: 'Agent integration, safety, and governance from the start' },
        { name: 'Replay Systems', desc: 'Action logging, state streams, and auditability' },
        { name: 'Fleet Architecture', desc: 'Scaling from one robot to many without rewrites' },
        { name: 'Message-Based Systems', desc: 'NATS, distributed coordination, and observability' }
      ]
    },
    {
      category: 'Autonomy & Safety',
      tools: [
        { name: 'Agent Orchestration', desc: 'Cloud and edge AI agent integration' },
        { name: 'Safety Constraints', desc: 'Runtime governance and emergency overrides' },
        { name: 'Capability Surfaces', desc: 'Tool calling and action abstraction for AI' },
        { name: 'Decision Auditing', desc: 'Full traceability of autonomous actions' }
      ]
    }
  ];

  return (
    <div className="services-page">
      {/* Background */}
      <div className="services-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
        <div className="glow-orb orb-1" />
        <div className="glow-orb orb-2" />
      </div>

      {/* Hero */}
      <section className="services-hero">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Services</span>
            <h1>Consulting & Development</h1>
            <p className="hero-subtitle">
              Building robot platforms for the physical AI future.
              We help teams architect, build, and deploy autonomous systems at scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-content">
        <div className="section-container">
          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="service-card"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
              >
                <span className="service-icon">{service.icon}</span>
                <h2>{service.title}</h2>
                <p className="service-description">{service.description}</p>
                <ul className="service-details">
                  {service.details.map((detail, i) => (
                    <li key={i}>{detail}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Tools Section */}
      <section className="ai-tools-section">
        <div className="section-container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Expertise</h2>
            <p className="section-subtitle">
              We specialize in systems where autonomy, orchestration, and fleet operations 
              are the hard problems—not low-level kinematics. If you're building robots where 
              decision-making is moving up the stack, we can help.
            </p>
          </motion.div>

          <div className="ai-tools-grid">
            {aiTools.map((category, index) => (
              <motion.div
                key={category.category}
                className="ai-category-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15, duration: 0.6 }}
              >
                <h3>{category.category}</h3>
                <div className="tools-list">
                  {category.tools.map((tool, i) => (
                    <div key={i} className="tool-item">
                      <span className="tool-name">{tool.name}</span>
                      <span className="tool-desc">{tool.desc}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="approach-section">
        <div className="section-container">
          <motion.div
            className="approach-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Our Approach</h2>
            <p>
              We bring software-first thinking to robotics. Instead of treating autonomy as 
              a hardware problem, we treat it as a distributed systems and AI orchestration 
              problem. The result: robots that are debuggable, auditable, and scalable.
            </p>
            <p>
              With decades of combined experience across enterprise software, robotics, 
              naval systems, and AI, we understand both the technical challenges and the 
              operational realities. We don't create dependency—we transfer capability 
              to your team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="services-cta-section">
        <div className="section-container">
          <motion.div
            className="cta-box"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Work With Us</h2>
            <p>
              Building robots where AI-driven autonomy is the hard problem? 
              Scaling from one robot to fleets? We'd love to discuss your challenge.
            </p>
            <div className="cta-buttons">
              <a href="mailto:gherlein@herlein.com" className="cta-primary">
                Contact Us
              </a>
              <Link to="/founders" className="cta-secondary">
                Meet the Team
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back Link */}
      <div className="section-container">
        <motion.div
          className="back-link"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Link to="/">← Back to Home</Link>
        </motion.div>
      </div>
    </div>
  );
};

export default ServicesPage;
